import React, { useEffect } from 'react';
import { DeckVisibilityToggle } from './DeckVisibilityToggle';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { EditorToolbar } from '../editor/EditorToolbar';
import { useI18n } from '../../contexts/I18nContext';

interface DeckFormHeaderProps {
  name: string;
  onNameChange: (name: string) => void;
  description?: string;
  onDescriptionChange?: (description: string) => void;
  isPublic: boolean;
  onVisibilityChange: (isPublic: boolean) => void;
  disabled?: boolean;
  title: string;
}

export function DeckFormHeader({
  name,
  onNameChange,
  description = '',
  onDescriptionChange = () => {},
  isPublic,
  onVisibilityChange,
  disabled,
  title
}: DeckFormHeaderProps) {
  const { t } = useI18n();
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
    ],
    content: description,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onDescriptionChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && description !== editor.getHTML()) {
      editor.commands.setContent(description);
    }
  }, [description, editor]);

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <DeckVisibilityToggle
            isPublic={isPublic}
            onChange={onVisibilityChange}
            disabled={disabled}
          />
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="deckName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('create_deck.form.deck_name')}
            </label>
            <input
              id="deckName"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              placeholder={t('create_deck.form.deck_name_placeholder')}
              required
              disabled={disabled}
            />
          </div>

          <div>
            <label htmlFor="deckDescription" className="block text-sm font-medium text-gray-700 mb-2">
              {t('create_deck.form.description')}
            </label>
            <div className="prose max-w-none">
              <EditorToolbar editor={editor} />
              <div className="mt-2 border rounded-md p-2 min-h-[200px]">
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}